/**
 * Set Notification Read UseCase
 * Flutter source: lib/data/repository/notifications_repository.dart:16-18
 * + lib/data/data_sources/notifications_data_source.dart:5-12
 *
 * Marks one notification as read. Flutter's repository takes the whole
 * `AppNotification` and the data source destructures
 * `notification.info.usersInfo.peerUserId` (the RECIPIENT — not `userId`, which
 * is the actor) and `notification.id`; this use case does the same resolution
 * and hands primitives to the service.
 *
 * The `if (!notification.info.read)` gate lives at the CALLER
 * (lib/services/push_navigation_service.dart:357-359) — on web the inbox
 * feature hook's `markNotificationRead` — never here and never in the page.
 *
 * A `true` return is the caller's signal to perform the optimistic local `read`
 * flip: Flutter flips `notification.info.read = true` only after the awaited
 * write returns (lib/presentation/screens/messages/messages_screen.dart:665-672);
 * if the write throws, the `await` propagates and the flip never runs. Returning
 * `false` here reproduces that — the hook skips its flip.
 */
import { NotificationsService } from '@/domain/services/notifications/notificationsService';
import type { AppNotificationEntity } from '@/domain/entities/notification/appNotificationEntity';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class SetNotificationReadUseCase {
  private notificationsService: NotificationsService;

  constructor(notificationsService: NotificationsService = NotificationsService.getInstance()) {
    this.notificationsService = notificationsService;
  }

  /**
   * @param notification - The notification to mark read.
   * @returns `true` when the write succeeded — the caller's signal to flip the
   *   local `read` flag; `false` on a missing id/recipient or a failed write.
   */
  async execute(notification: AppNotificationEntity): Promise<boolean> {
    try {
      const ownerUserId = notification.info.usersInfo.peerUserId;
      const notificationId = notification.id;

      // Flutter null-asserts both (`doc(...peerUserId)` / `doc(notification.id)`);
      // both are optional on the web entity, so this guard is the defensive
      // equivalent rather than a behaviour change.
      if (!ownerUserId || !notificationId) {
        return false;
      }

      return await this.notificationsService.setNotificationRead(ownerUserId, notificationId);
    } catch (error) {
      appLogger.error(
        'Error setting notification read',
        'SetNotificationReadUseCase.execute',
        { error: error as Error },
      );
      return false;
    }
  }
}

export const setNotificationReadUseCase = new SetNotificationReadUseCase();
